import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { routes } from '../../../../utils/constants/ownerPensionerRoutes';
import { useSocketContext } from '../../../../contexts/SocketContext';
import { profileLockMessage } from '../../utils/profileLockMessage';
import { editingLocked } from '../../../../utils/editingLocked';
import { formatPhoneNumber } from '../../../../utils/formatPhoneNumber';
import { CardTitle } from '../../../../components/CardTitle/CardTitle';
import { InfoGrid } from '../../../../components/InfoGrid/InfoGrid';

/**
 * Renders a card component for displaying groomer information.
 *@component
 *
 * @param {Object} props - The component props.
 * @param {Array} props.groomers - The array of groomer objects.
 * @param {function} props.navigate - The function to navigate to a different page.
 * @param {Object} props.owner - The owner object.
 * @param {boolean} props.isFieldsLoading - Indicates if the fields are loading.
 * @returns {JSX.Element} The groomer card component.
 */
export const GroomerCard = ({ groomers, navigate, owner, isFieldsLoading }) => {
  const { t } = useTranslation();
  const { profileLock } = useSocketContext();

  const createPath = `${routes?.VETERINARIAN_GROOMER}/create/groomer/${owner?._id}`;
  const editPath = `${routes?.VETERINARIAN_GROOMER}/edit/groomer/${groomers?.[0]?._id}`;

  const items = groomers
    ? [
        {
          label: t('groomers.form.name'),
          content: groomers.name || '-'
        },
        {
          label: t('groomers.postal_address'),
          content: groomers.address
            ? `${groomers?.address?.number || ''} ${
                groomers?.address?.street || ''
              }`
            : '-'
        },
        {
          label: t('groomers.form.address.city'),
          content: groomers?.address ? groomers?.address?.city : '-'
        },
        {
          label: t('groomers.form.address.country'),
          content: groomers?.address ? groomers?.address?.country : '-'
        },
        {
          label: t('groomers.form.phone_number.number'),
          content: groomers?.phone_number?.number
            ? formatPhoneNumber(groomers.phone_number)
            : '-'
        }
      ]
    : [];

  return (
    <Card
      className="vet-card"
      title={
        <CardTitle icon="medicine">{t('groomers.header_panel')}</CardTitle>
      }
      extra={
        <Button
          type="primary"
          onClick={() => {
            if (editingLocked(profileLock, owner?._id)) {
              profileLockMessage(t, 'profile');
              return;
            }
            navigate(`${groomers ? editPath : createPath}`);
          }}
        >
          {groomers ? <EditOutlined /> : <PlusOutlined />}
          {groomers ? t('buttons.edit') : t('buttons.create')}
        </Button>
      }
    >
      {groomers ? (
        <Skeleton loading={isFieldsLoading} active>
          <InfoGrid items={items.flat(1)} />
        </Skeleton>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('groomers.no_groomer')}
        />
      )}
    </Card>
  );
};

GroomerCard.propTypes = {
  groomers: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string,
    postal_code: PropTypes.string,
    address: PropTypes.shape({
      number: PropTypes.string,
      street: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string
    }),
    phone_number: PropTypes.shape({
      country_code: PropTypes.string,
      number: PropTypes.string
    })
  }),
  navigate: PropTypes.func.isRequired,
  owner: PropTypes.shape({
    _id: PropTypes.string.isRequired
  }).isRequired,
  isFieldsLoading: PropTypes.bool.isRequired
};

GroomerCard.defaultProps = {
  groomers: {}
};
